import { NgIf } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { GeoPoint, Firestore } from '@angular/fire/firestore';
import {
  getDownloadURL,
  ref,
  Storage,
  uploadBytesResumable,
} from '@angular/fire/storage';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { select, Store } from '@ngrx/store';
import {
  UppyAngularDashboardModalModule,
  UppyAngularDashboardModule,
  UppyAngularDragDropModule,
  UppyAngularProgressBarModule,
  UppyAngularStatusBarModule,
} from '@uppy/angular';
import Uppy from '@uppy/core/lib/Uppy';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { Member } from '../../../../models/member.model';
import { UserModel } from '../../../../models/user.model';
import { AuthService } from '../../../../services/auth.service';
import * as memberActions from '../../../../state/actions/member.actions';
import { AppState } from '../../../../state/appstate';
import * as memberSelector from '../../../../state/selectors/member.selector';
import { ContactComponent } from '../../../components/dropdowns/contact/contact.component';
import { CountryComponent } from '../../../components/dropdowns/country/country.component';
import { GenderComponent } from '../../../components/dropdowns/gender/gender.component';
import { AddLocationComponent } from '../../../components/maps/add-location/add-location.component';
import { LoadingDotsComponent } from '../../../components/spinners/loading-dots/loading-dots.component';
import { CheckboxComponent } from '../../../components/inputs/checkbox/checkbox.component';

@Component({
  selector: 'app-edit-member',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    GenderComponent,
    CountryComponent,
    ContactComponent,
    LoadingDotsComponent,
    NgIf,
    UppyAngularDashboardModule,
    UppyAngularDashboardModalModule,
    UppyAngularDragDropModule,
    UppyAngularProgressBarModule,
    UppyAngularStatusBarModule,
    AddLocationComponent,
    CheckboxComponent,
  ],
  templateUrl: './edit-member.component.html',
  styleUrls: ['./edit-member.component.css'],
})
export class EditMemberComponent implements OnInit, OnDestroy {
  private readonly storage: Storage = inject(Storage);
  private readonly afs: Firestore = inject(Firestore);

  user: UserModel | null = null;
  userSub: Subscription | undefined;

  message: string = '';
  error: string | null = null;

  today: Date = new Date();

  groupId: string | null = null;
  memberId: string | null = null;

  member: Member | null | undefined;

  pageForm: FormGroup;

  working: boolean = false;
  progressSub: Subscription | undefined;

  uppy: Uppy = new Uppy({
    autoProceed: false,
    restrictions: {
      maxFileSize: 5242880, // 5 MB
      maxNumberOfFiles: 1,
      minNumberOfFiles: 1,
      allowedFileTypes: ['.jpg', '.jpeg', '.png'],
    },
  });

  dashboardProps = {
    inline: true,
    hideUploadButton: true,
    showSelectedFiles: true,
    proudlyDisplayPoweredByUppy: false,
    width: '100%',
    height: '130px',
    note: 'Images only (jpg, jpeg, png), up to 5 MB. Only one image is allowed.',
  };

  constructor(
    private store: Store<AppState>,
    private auth: AuthService,
    private toastr: ToastrService,
    private _activatedRoute: ActivatedRoute
  ) {
    this._activatedRoute.paramMap.subscribe((params) => {
      this.groupId = params.get('id');
      this.memberId = params.get('memberId');
    });

    this.pageForm = new FormGroup({
      name: new FormControl('', [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(100),
      ]),
      profession: new FormControl('', [
        Validators.minLength(5),
        Validators.maxLength(100),
      ]),
      contact: new FormControl({}),
      email: new FormControl('', [Validators.email]),
      dob: new FormControl(),
      showDob: new FormControl(false),
      gender: new FormControl('unspecified'),
      buildingNo: new FormControl('', [
        Validators.minLength(1),
        Validators.maxLength(10),
      ]),
      buildingName: new FormControl('', [
        Validators.minLength(5),
        Validators.maxLength(100),
      ]),
      addressLine1: new FormControl('', [
        Validators.minLength(5),
        Validators.maxLength(100),
      ]),
      addressLine2: new FormControl('', [
        Validators.minLength(5),
        Validators.maxLength(100),
      ]),
      city: new FormControl('', [
        Validators.required,
        Validators.maxLength(100),
      ]),
      country: new FormControl('', [Validators.required]),
      position: new FormControl(''),
    });
  }

  ngOnInit(): void {
    this.store
      .select(memberSelector.getMemberById(this.memberId!))
      .subscribe((member) => {
        this.member = member;
        this.pageForm.patchValue({
          name: this.member?.name,
          profession: this.member?.profession,
          contact: this.member?.contact,
          dob: this.member?.dob
            ? moment(this.member?.dob.toDate()).format('YYYY-MM-DD')
            : null,
          showDob: this.member?.showDob ?? false,
          gender: this.member?.gender ?? 'unspecified',
          email: this.member?.email ?? '',
          buildingNo: this.member?.address?.buildingNo,
          buildingName: this.member?.address?.buildingName,
          addressLine1: this.member?.address?.addressLine1,
          addressLine2: this.member?.address?.addressLine2,
          city: this.member?.address?.city,
          country: this.member?.address?.country,
          position: this.member?.location?.position,
        });
      });

    this.userSub = this.store
      .select((state) => state.user.user)
      .subscribe((user) => {
        this.user = user;
      });

    this.progressSub = this.store
      .select((state) => state.members)
      .pipe(select(memberSelector.inProgress))
      .subscribe((inProgress) => {
        this.working = inProgress;
      });
  }

  ngOnDestroy(): void {
    this.userSub?.unsubscribe();
    this.progressSub?.unsubscribe();
  }

  setLocation(position: GeoPoint | null) {
    this.pageForm.patchValue({ position });
  }

  update() {
    if (this.pageForm.invalid) return;

    if (this.groupId == null || this.member == null) {
      this.error = 'Group or Member identifier is null';
      return;
    }

    if (this.user == null) {
      this.error = 'User not found. Kindly login again.';
    }

    const file: any = this.uppy.getFiles()[0];

    const uid = this.auth.getUid();

    if (file != null && uid != null) {
      this.working = true;

      const fileExtn = file.name.split('.').pop();

      const storageRef = ref(
        this.storage,
        `unregistered/${this.groupId}/${this.member?.memberId}.${fileExtn}`
      );

      const metadata = {
        contentType: file.type,
        customMetadata: {
          uid: uid!,
          groupId: this.groupId,
        },
      };

      const uploadTask = uploadBytesResumable(storageRef, file.data, metadata);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          this.message = `${progress.toFixed(2)}%`;
        },
        (error) => {
          this.toastr.error(
            'Member image could not be updated. Please try again later.',
            'Image upload Failed'
          );
          this.working = false;
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            this.updateDatabaseEntry(downloadURL);
          });
        }
      );
    } else {
      // Update group without Image
      this.updateDatabaseEntry();
    }
  }

  updateDatabaseEntry(downloadURL?: string) {
    var temp: any = {
      ...this.member,
      name: this.pageForm.get('name')?.value,
      unregistered: true,
      gender: this.pageForm.get('gender')?.value ?? 'unspecified',
      address: {
        buildingNo: this.pageForm.get('buildingNo')?.value,
        buildingName: this.pageForm.get('buildingName')?.value,
        addressLine1: this.pageForm.get('addressLine1')?.value,
        addressLine2: this.pageForm.get('addressLine2')?.value,
        city: this.pageForm.get('city')?.value,
        country: this.pageForm.get('country')?.value,
      },
    };

    if (this.pageForm.get('email')?.value) {
      temp.email = this.pageForm.get('email')?.value;
    }

    if (this.pageForm.get('profession')?.value) {
      temp.profession = this.pageForm.get('profession')?.value;
    }

    if (this.pageForm.get('dob')?.value != null) {
      temp.dob = moment(this.pageForm.get('dob')?.value).toDate();
    } else {
      temp.dob = null;
    }

    temp.showDob = this.pageForm.get('showDob')?.value ?? false;

    if (this.pageForm.get('contact')?.value.contact) {
      temp.contact = this.pageForm.get('contact')?.value;
    }

    if (this.pageForm.get('position')?.value) {
      temp.location = {
        id: this.member?.memberId,
        name: this.pageForm.get('name')?.value,
        position: this.pageForm.get('position')?.value,
      };
    }

    if (downloadURL) {
      temp.photoURL = downloadURL;
    }

    temp.groupId = this.groupId;
    temp.memberId = this.member?.memberId;
    temp.modified = new Date();
    temp.approvedById = this.user?.uid;



    this.store.dispatch(
      memberActions.update({ id: this.member?.id!, member: temp })
    );
  }
}
